import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx-js-style";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "./CustomButton";
import Loader from "../dist/Loader";

const LeaveRemarksPage = () => {
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("All");
  const [monthYearOptions, setMonthYearOptions] = useState([]);
  const [selectedMonthYear, setSelectedMonthYear] = useState("");
  const [leaveData, setLeaveData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [editedLeaveType, setEditedLeaveType] = useState("");
  const [editedHrStatus, seteditedHrStatus] = useState("");
  const [editedHrRemarks, setEditedHrRemarks] = useState("");
  const [isLoading, setIsLoading] = useState(false);

 useEffect(() => {
  const today = new Date();

  const months = Array.from({ length: 12 }, (_, i) =>
    new Date(today.getFullYear(), i, 1).toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
    })
  );

  const defaultMonth = today.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });

  setMonthYearOptions(months);
  setSelectedMonthYear(defaultMonth);

  axios
    .get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() })
    .then((res) => setStates(res.data));
}, []);

  // FIXED: Loader + State Sync + API Call
  const handleFilter = async () => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 50)); // ensures updated state sync

    try {
      const response = await axios.get(`${BASE_URL}/api/leave-applications`, {
        headers: getAuthHeaders(),
        params: {
          state: selectedState !== "All" ? selectedState : null,
          monthYear: selectedMonthYear,
        },
      });

      setLeaveData(response.data);
      setShowTable(true);
    } catch (err) {
      console.error(err);
    }

    setIsLoading(false);
  };

  const handlePrint = () => {
    const printContents = document.getElementById("printable-table")?.innerHTML;
    if (!printContents) return;

    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
  };

  const handleExportToExcel = () => {
    const exportData = leaveData.map((row, index) => ({
      "S.No": index + 1,
      "Emp ID": row.employee,
      Employee: row.employee_name,
      Division: row.division,
      DOJ: row.doj || "",
      DOR: row.dor || "",
      State: row.state,
      City: row.city,
      Type: row.leave_type,
      "Emp Remarks": (row.empremarks || "").replace(/\r?\n|\r/g, " "),
      "Start Date": row.start_date,
      "End Date": row.end_date,
      "Duration (Day)": row.duration,
      Manager: row.manager,
      "HR Status": row.hr_status,
      "HR Remarks": (row.hr_remarks || "").replace(/\r?\n|\r/g, " "),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    const headers = Object.keys(exportData[0] || {});
    const totalCols = headers.length;
    const totalRows = exportData.length + 1;

    worksheet["!ref"] = XLSX.utils.encode_range(
      { r: 0, c: 0 },
      { r: totalRows - 1, c: totalCols - 1 }
    );

    const headerStyle = {
      font: { bold: true, sz: 12 },
      fill: { fgColor: { rgb: "D9D9D9" } },
      alignment: { horizontal: "center", wrapText: true },
      border: { top: { style: "thin" }, bottom: { style: "thin" } },
    };

    const rowStyle = {
      fill: { fgColor: { rgb: "E2EFDA" } },
      alignment: { wrapText: true },
      border: { top: { style: "thin" }, bottom: { style: "thin" } },
    };

    headers.forEach((_, colIndex) => {
      const address = XLSX.utils.encode_cell({ r: 0, c: colIndex });
      worksheet[address].s = headerStyle;
    });

    for (let r = 1; r < totalRows; r++) {
      for (let c = 0; c < totalCols; c++) {
        const address = XLSX.utils.encode_cell({ r, c });
        if (!worksheet[address]) worksheet[address] = { t: "s", v: "" };
        worksheet[address].s = rowStyle;
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leave Remarks");
    XLSX.writeFile(
      workbook,
      `Leave_Remarks_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "";

  const fetchLeaveTypes = async () => {
    const response = await axios.get(`${BASE_URL}/api/leave-types`, {
      headers: getAuthHeaders(),
    });
    setLeaveTypes(response.data);
  };

  const handleSave = async (id) => {
    const selectedType = leaveTypes.find((t) => t.name === editedLeaveType);
    if (!selectedType) return alert("Invalid leave type selected");

    await axios.put(
      `${BASE_URL}/api/leave-applications/${id}`,
      {
        hr_status: editedHrStatus,
        hrremarks: editedHrRemarks,
        leavetype_id: selectedType.id,
      },
      { headers: getAuthHeaders() }
    );

    setEditingId(null);
    handleFilter();
  };

  const handleDelete = async (id) => {
    await axios.delete(`${BASE_URL}/api/leave-applications/${id}`, {
      headers: getAuthHeaders(),
    });
    alert("Deleted successfully");
    handleFilter();
  };

  return (
    <div className="min-h-screen w-full px-2 sm:px-4 py-3 bg-gray-50">
      {/* TOP FILTER CARD */}
      <div className="bg-white border-2 border-gray-300 rounded-lg shadow-md p-4">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">Employee </span>
          <span className="text-blue-500">Leave Report</span>
        </h1>

        <div className="border border-gray-300 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* STATE */}
            <div className="flex flex-1 items-center gap-2">
              <label className="font-bold text-sm text-gray-700 whitespace-nowrap">
                State
              </label>
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="All">All</option>
                {states.map((s) => (
                  <option key={s.id} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {/* MONTH YEAR */}
            <div className="flex flex-1 items-center gap-2">
              <label className="font-bold text-sm text-gray-700 whitespace-nowrap">
                Month/Year
              </label>
              <select
                value={selectedMonthYear}
                onChange={(e) => setSelectedMonthYear(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              >
                {monthYearOptions.map((m, i) => (
                  <option key={i} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* GO BUTTON */}
          <div className="flex justify-center">
            <button
              onClick={handleFilter}
              disabled={isLoading}
              className={`flex items-center justify-center w-32 mt-3 px-4 py-2 rounded-lg text-white 
              ${
                isLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              }`}
            >
              {isLoading && (
                <Loader className="h-5 w-5 animate-spin border-t-2 border-white" />
              )}
              {isLoading ? "Loading..." : "GO"}
            </button>
          </div>
        </div>
      </div>

      {/* TABLE */}
      {showTable && (
        <div className="mt-5 bg-white border border-gray-300 rounded-lg p-4 shadow-md">
          <h2 className="text-xl font-bold text-center text-gray-800 mb-4">
            <span className="text-blue-700">Approved </span>
            <span className="text-blue-500">Leaves</span>
          </h2>

          <div
            id="printable-table"
            className="overflow-x-auto border rounded-md"
          >
            <table className="min-w-full text-xs text-center border-collapse">
              <thead className="bg-blue-500 text-white">
                <tr>
                  {[
                    "S.No",
                    "Delete",
                    "Emp ID",
                    "Employee",
                    "Division",
                    "DOJ",
                    "DOR",
                    "State",
                    "City",
                    "Type",
                    "Emp Remarks",
                    "Start Date",
                    "End Date",
                    "Duration (Day)",
                    "Manager",
                    "HR Status",
                    "HR Remarks",
                    "Edit",
                  ].map((h, i) => (
                    <th
                      key={i}
                      className={`px-2 py-2 border ${
                        h === "Delete" || h === "Edit" ? "print:hidden" : ""
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {leaveData.map((row, index) => (
                  <tr key={row.id} className="even:bg-gray-50 hover:bg-blue-50">
                    <td className="border px-2 py-1">{index + 1}</td>

                    {/* DELETE */}
                    <td className="border px-2 py-1 print:hidden">
                      <button
                        onClick={() => handleDelete(row.id)}
                        className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded-md w-full"
                      >
                        Delete
                      </button>
                    </td>

                    <td className="border px-2 py-1">{row.employee}</td>
                    <td className="border px-2 py-1">{row.employee_name}</td>
                    <td className="border px-2 py-1">{row.division}</td>
                    <td className="border px-2 py-1">{formatDate(row.doj)}</td>
                    <td className="border px-2 py-1">{formatDate(row.dor)}</td>
                    <td className="border px-2 py-1">{row.state}</td>
                    <td className="border px-2 py-1">{row.city}</td>

                    {/* LEAVE TYPE */}
                    <td className="border px-2 py-1">
                      {editingId === row.id ? (
                        <select
                          value={editedLeaveType}
                          onChange={(e) => setEditedLeaveType(e.target.value)}
                          className="border rounded-md px-2 py-1"
                        >
                          {leaveTypes.map((t) => (
                            <option key={t.id} value={t.name}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        row.leave_type
                      )}
                    </td>

                    <td className="border px-2 py-1 text-left">
                      {row.empremarks}
                    </td>

                    <td className="border px-2 py-1">
                      {formatDate(row.start_date)}
                    </td>
                    <td className="border px-2 py-1">
                      {formatDate(row.end_date)}
                    </td>

                    <td className="border px-2 py-1">{row.duration}</td>
                    <td className="border px-2 py-1">{row.manager}</td>

                    {/* HR STATUS */}
                    <td className="border px-2 py-1">
                      {editingId === row.id ? (
                        <div className="flex flex-col gap-1">
                          <label>
                            <input
                              type="radio"
                              checked={editedHrStatus === true}
                              onChange={() => seteditedHrStatus(true)}
                            />{" "}
                            Yes
                          </label>
                          <label>
                            <input
                              type="radio"
                              checked={editedHrStatus === false}
                              onChange={() => seteditedHrStatus(false)}
                            />{" "}
                            No
                          </label>
                        </div>
                      ) : (
                        row.hr_status
                      )}
                    </td>

                    {/* HR REMARKS */}
                    <td className="border px-2 py-1 text-left">
                      {editingId === row.id ? (
                        <textarea
                          className="w-full border rounded-md p-1"
                          value={editedHrRemarks}
                          onChange={(e) => setEditedHrRemarks(e.target.value)}
                        />
                      ) : (
                        row.hr_remarks
                      )}
                    </td>

                    <td className="border px-2 py-1 print:hidden">
                      {editingId === row.id ? (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleSave(row.id)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded-md"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="bg-orange-500 hover:bg-orange-600 text-white px-2 py-1 rounded-md"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            fetchLeaveTypes();
                            setEditingId(row.id);
                            setEditedLeaveType(row.leave_type);
                            seteditedHrStatus(row.hr_status === "Yes");
                            setEditedHrRemarks(row.hr_remarks || "");
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded-md"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ACTION BUTTONS */}
          <div className="mt-4 flex gap-4 justify-center">
            <CustomButton onClick={handlePrint} label="Print" variant="print" />
            <CustomButton
              onClick={handleExportToExcel}
              label="Export"
              variant="export"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveRemarksPage;
